import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, Loading } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, UserPlus, Store, Home, Users } from "lucide-react";
import { toast } from "sonner";

const empty = { type: "home", first_name: "", last_name: "", company_name: "", referent: "", phone: "", email: "", vat_cf: "", address: "", notes: "", partner_id: null };

export default function Customers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [openDlg, setOpenDlg] = useState(false);
  const [form, setForm] = useState(empty);
  const [partners, setPartners] = useState([]);

  const load = () => {
    const params = new URLSearchParams();
    if (tab !== "all") params.set("type", tab);
    if (q) params.set("q", q);
    api.get(`/customers?${params}`).then((r) => setItems(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [tab, q]);
  useEffect(() => { if (user.role === "super_admin") api.get("/partners").then((r) => { setPartners(r.data); setForm((f) => ({ ...f, partner_id: r.data[0]?.id })); }); }, []);

  const save = async () => {
    try {
      await api.post("/customers", form);
      toast.success("Cliente creato");
      setOpenDlg(false); setForm({ ...empty, partner_id: partners[0]?.id }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Clienti" subtitle="Anagrafiche HOME e HOST">
        <Dialog open={openDlg} onOpenChange={setOpenDlg}>
          <DialogTrigger asChild><Button data-testid="add-customer-btn" className="bg-[#0B2447] hover:bg-[#14315f]"><UserPlus size={18} className="mr-2" />Nuovo cliente</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Nuovo cliente</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div><Label>Tipologia</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="customer-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="home">HOME (privato)</SelectItem><SelectItem value="host">HOST (struttura)</SelectItem></SelectContent>
                </Select>
              </div>
              {user.role === "super_admin" && (
                <div><Label>Partner</Label>
                  <Select value={form.partner_id || ""} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Seleziona partner" /></SelectTrigger>
                    <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.type === "home" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nome</Label><Input data-testid="cust-first" className="mt-1" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                  <div><Label>Cognome</Label><Input data-testid="cust-last" className="mt-1" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
                </div>
              ) : (
                <>
                  <div><Label>Denominazione</Label><Input data-testid="cust-company" className="mt-1" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>
                  <div><Label>Referente</Label><Input className="mt-1" value={form.referent} onChange={(e) => setForm({ ...form, referent: e.target.value })} /></div>
                  <div><Label>P.IVA / Cod. Fiscale</Label><Input className="mt-1" value={form.vat_cf} onChange={(e) => setForm({ ...form, vat_cf: e.target.value })} /></div>
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Telefono</Label><Input data-testid="cust-phone" className="mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input className="mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div><Label>Indirizzo</Label><Input className="mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>Note</Label><Textarea className="mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="save-customer" className="bg-[#0B2447]">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList><TabsTrigger value="all" data-testid="tab-all">Tutti</TabsTrigger><TabsTrigger value="home" data-testid="tab-home">HOME</TabsTrigger><TabsTrigger value="host" data-testid="tab-host">HOST</TabsTrigger></TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="pl-9" />
        </div>
      </div>

      {!items ? <Loading /> : items.length === 0 ? <EmptyState icon={Users} title="Nessun cliente" /> : (
        <Card className="overflow-hidden">
          <div className="divide-y">
            {items.map((c) => (
              <button key={c.id} data-testid={`customer-row-${c.code}`} onClick={() => nav(`/clienti/${c.id}`)} className="w-full flex items-center gap-4 px-4 py-3.5 hover:bg-slate-50 text-left">
                <span className={`p-2 rounded-lg ${c.type === "host" ? "bg-[#E4F5FB] text-[#0EA5C4]" : "bg-slate-100 text-slate-500"}`}>{c.type === "host" ? <Store size={16} /> : <Home size={16} />}</span>
                <div className="flex-1 min-w-0"><p className="font-semibold text-[#0B2447] truncate">{c.display_name}</p><p className="text-xs text-slate-400">{c.code} · {c.phone || "—"} · {c.email || "—"}</p></div>
                <span className="hidden sm:block text-xs uppercase font-bold text-slate-400">{c.type}</span>
                <StatusBadge status={c.status} />
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
